import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretDown, CaretRight, ShoppingCart, Receipt } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { assetProcurementApi, stashOrderPrefill } from '@/lib/assetProcurementApi';

const yen = (v) => (v == null ? '—' : `¥${Number(v).toLocaleString('ja-JP')}`);

const Metric = ({ label, value, sub, testId }) => (
  <div className="rounded-md border border-zinc-200 bg-white px-3 py-2" data-testid={testId}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">{label}</div>
    <div className="text-sm font-bold text-zinc-900 tabular-nums">{value}</div>
    {sub && <div className="text-[10px] text-zinc-500">{sub}</div>}
  </div>
);

/**
 * PurchaseSummaryCard — 台帳行の購入実績（累計購入額・最終/平均単価・購入履歴）。
 *
 * 数値は購入価格履歴（入荷確認が書く正本）から毎回集計した結果をそのまま出す。
 * 閲覧には台帳の編集権限が必要 — 403 のときはカード自体を出さない。
 *
 * `orderPrefill` を渡すと「この資産を発注」が出る。部品受注入力の新規依頼へ
 * 既存のマスター選択（item_id + item_source）の形で引き継ぐので、入荷時の
 * 原価はこの行に確実に戻る。
 */
export const PurchaseSummaryCard = ({ ledger, assetId, orderPrefill = null, showHistory = true, className = '' }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!ledger || !assetId) return;
    let cancelled = false;
    setData(null); setDenied(false);
    assetProcurementApi.purchaseSummary(ledger, assetId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setDenied(e?.response?.status === 403); });
    return () => { cancelled = true; };
  }, [ledger, assetId]);

  if (denied || !data) return null;

  const order = () => {
    stashOrderPrefill(orderPrefill);
    navigate('/parts-request?tab=new');
  };

  return (
    <div className={`rounded-lg border border-zinc-200 bg-zinc-50/70 p-3 ${className}`}
      data-testid={`purchase-summary-${ledger}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-xs font-bold text-zinc-700">
          <Receipt size={14} weight="duotone" className="text-teal-700" /> 購入実績
          <span className="font-normal text-[10px] text-zinc-400">入荷確認の実績から集計（編集権限者のみ）</span>
        </div>
        {orderPrefill && (
          <Button size="sm" variant="outline" onClick={order}
            className="h-7 text-[11px] border-teal-300 text-teal-800 hover:bg-teal-50"
            data-testid={`order-this-asset-${ledger}`}>
            <ShoppingCart size={12} weight="bold" className="mr-1" /> この資産を発注
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Metric label="累計購入額（税抜）" value={yen(data.total_excl_tax)}
          sub={data.tax_unknown_count > 0
            ? `税込 ${yen(data.total_incl_tax)}（税区分未設定 ${data.tax_unknown_count}件を含む）`
            : `税込 ${yen(data.total_incl_tax)}`}
          testId={`purchase-total-${ledger}`} />
        <Metric label="最終購入単価" value={yen(data.last_unit_price)}
          sub={[data.last_date, data.last_supplier].filter(Boolean).join(' · ') || null}
          testId={`purchase-last-${ledger}`} />
        <Metric label="平均購入単価" value={yen(data.avg_unit_price)}
          sub={data.qty_total ? `数量計 ${data.qty_total}` : null}
          testId={`purchase-avg-${ledger}`} />
        <Metric label="購入回数" value={`${data.count}件`}
          sub={data.registered_cost != null ? `取得額（手入力） ${yen(data.registered_cost)}` : '取得額（手入力） —'}
          testId={`purchase-count-${ledger}`} />
      </div>

      {showHistory && data.count > 0 && (
        <>
          <button type="button" onClick={() => setOpen((v) => !v)}
            className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-teal-800 hover:underline"
            data-testid={`purchase-history-toggle-${ledger}`}>
            {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
            購入履歴 {data.count}件
          </button>
          {open && (
            <div className="mt-2 overflow-x-auto rounded-md border border-zinc-200 bg-white">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-zinc-100 text-zinc-600">
                    <th className="text-left px-2 py-1.5">入荷日</th>
                    <th className="text-left px-2 py-1.5">依頼No</th>
                    <th className="text-right px-2 py-1.5">数量</th>
                    <th className="text-right px-2 py-1.5">単価</th>
                    <th className="text-right px-2 py-1.5">金額(税抜)</th>
                    <th className="text-right px-2 py-1.5">金額(税込)</th>
                    <th className="text-left px-2 py-1.5">仕入先</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} className="border-t border-zinc-100"
                      data-testid={`purchase-row-${r.id}`}>
                      <td className="px-2 py-1.5 whitespace-nowrap">{r.received_date || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{r.request_no || '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.qty ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{yen(r.unit_price)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{yen(r.amount_excl_tax)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-zinc-500">
                        {r.amount_incl_tax == null ? '税区分未設定' : yen(r.amount_incl_tax)}
                      </td>
                      <td className="px-2 py-1.5 truncate max-w-32">{r.supplier || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PurchaseSummaryCard;
